<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" :hide-details="true" />
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="fields.layout.gap"
                  type="number"
                  label="Gap between images and content"
                  min="0"
                  suffix="px"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="fields.images.position"
                  label="Images position (desktop)"
                  :items="positionOptions"
                  item-text="text"
                  item-value="value"
                  hide-details
                />
              </v-col>
            </v-row>
            <p class="text-caption text--secondary mt-2 mb-0">
              On mobile, images are always displayed above content. Gap is always 24px on mobile.
            </p>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Image Collage</label>
          <div class="group-item">
            <p class="text-caption text--secondary mb-3">Image 1 overlaps Image 2, Image 2 overlaps Image 3.</p>
            <div v-for="(item, index) in fields.images.items" :key="index" class="mb-4">
              <label class="label-input" style="font-size: 16px">Image {{ index + 1 }}</label>
              <UploadImage
                @callback="obj => handleUploadCollageImage(index, obj)"
                :image="{ imageSrc: item.src }"
                :height="`160`"
                :typeUpload="'image'"
                @callbackDelete="() => handleRemoveCollageImage(index)"
              />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Content</label>
          <ckeditor :editor="editor" v-model="fields.content" :config="editorConfig" />
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0" for="signatureBox">Signature</label>
            <input type="checkbox" v-model="fields.signature.show" id="signatureBox" />
          </div>
          <div class="group-item">
            <UploadImage
              @callback="handleUploadSignature"
              :image="{ imageSrc: fields.signature.src }"
              :height="`120`"
              :typeUpload="'image'"
              @callbackDelete="handleRemoveSignature"
            />
          </div>
        </div>

        <div class="mb-4">
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import ButtonPrimary from './common/button-primary.vue';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';

export default {
  name: 'OrthopedicCardBasicSectionEditor',
  components: {
    SectionSettingsContainerFuild,
    ButtonPrimary,
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Orthopedic card basic',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        layout: {
          gap: 80,
        },
        images: {
          position: 'left',
          items: [{ src: '' }, { src: '' }, { src: '' }],
        },
        content: '',
        signature: {
          show: false,
          src: '',
        },
        button: {
          show: true,
          backgroundColor: '#006196',
          color: '#FFFFFF',
          radius: 12,
          text: 'Shop Now',
          link: '#',
          iconPrefix: { show: false, src: '' },
          iconSuffix: { show: false, src: '' },
          subBtn: { show: false, text: '' },
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        layout: { ...this.fields.layout, ...(extract.layout || {}) },
        images: {
          ...this.fields.images,
          ...(extract.images || {}),
          items: (extract.images && extract.images.items) || this.fields.images.items,
        },
        signature: { ...this.fields.signature, ...(extract.signature || {}) },
        button: {
          ...this.fields.button,
          ...(extract.button || {}),
          iconPrefix: { ...this.fields.button.iconPrefix, ...((extract.button || {}).iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...((extract.button || {}).iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...((extract.button || {}).subBtn || {}) },
        },
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionOrthopedicCardBasic(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadCollageImage(index, obj) {
      this.$set(this.fields.images.items[index], 'src', (obj && obj.imageSrc) || '');
    },
    handleRemoveCollageImage(index) {
      this.$set(this.fields.images.items[index], 'src', '');
    },
    handleUploadSignature(obj) {
      this.fields.signature.src = (obj && obj.imageSrc) || '';
    },
    handleRemoveSignature() {
      this.fields.signature.src = '';
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
